import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { echartsDefaultTooltipFormatter } from "../../utils/dynamicDashboard";
import {
  getAggregatedFinancialContributors,
  getAggregatedFinancialTotals,
  getFinancialPartitionDiagrams,
  getFinancialTotalsTimeline,
} from "../../utils/Modernization";
import { getCSSVar } from "../../utils/styling";
import { DynamicEchartsChart } from "../chart/DynamicChartMemoized";

const getChartColors = () => ({
  defaultCosts: getCSSVar("--color-purpose-financial-dashboard-default-costs"),
  defaultSavings: getCSSVar("--color-purpose-financial-dashboard-default-savings"),
  energySavings: getCSSVar("--color-purpose-financial-dashboard-energy-savings"),
  foreignCapital: getCSSVar("--color-purpose-financial-dashboard-foreign-capital"),
  foreignCapitalCost: getCSSVar("--color-purpose-financial-dashboard-foreign-capital-cost"),
  heatEnergySavings: getCSSVar("--color-purpose-financial-dashboard-heat-energy-savings"),
  loanRepayment: getCSSVar("--color-purpose-financial-dashboard-loan-repayment"),
  maintenanceCosts: getCSSVar("--color-purpose-financial-dashboard-maintenance-costs"),
  otherSavings: getCSSVar("--color-purpose-financial-dashboard-other-savings"),
  ownCapital: getCSSVar("--color-purpose-financial-dashboard-own-capital"),
  servicesCosts: getCSSVar("--color-purpose-financial-dashboard-services-costs"),
  subsidies: getCSSVar("--color-purpose-financial-dashboard-subsidies"),
  totalCosts: getCSSVar("--color-purpose-financial-dashboard-total-costs"),
  totalSavings: getCSSVar("--color-purpose-financial-dashboard-total-savings"),
  totalTrend: getCSSVar("--color-purpose-financial-dashboard-total-trend"),
});

const getFinancialContributorMeta = ({ chartColors, unit }) => {
  return {
    energySavings: { color: chartColors.energySavings, name: `Energy Savings [${unit}]` },
    foreignCapital: { color: chartColors.foreignCapital, name: `Foreign Capital [${unit}]` },
    foreignCapitalCost: { color: chartColors.foreignCapitalCost, name: `Foreign Capital Cost [${unit}]` },
    heatEnergySavings: { color: chartColors.heatEnergySavings, name: `Heat Energy Savings [${unit}]` },
    loanRepayment: { color: chartColors.loanRepayment, name: `Loan Repayment [${unit}]` },
    maintenanceCosts: { color: chartColors.maintenanceCosts, name: `Maintenance Costs [${unit}]` },
    otherSavings: { color: chartColors.otherSavings, name: `Other Savings [${unit}]` },
    ownCapital: { color: chartColors.ownCapital, name: `Own Capital [${unit}]` },
    servicesCosts: { color: chartColors.servicesCosts, name: `Services Costs [${unit}]` },
    subsidies: { color: chartColors.subsidies, name: `Subsidies [${unit}]` },
  };
};

const DETAILED_DIAGRAMS_CONFIG = [
  { dataKey: "foreignCapital", group: "costs" },
  { dataKey: "ownCapital", group: "costs" },
  { dataKey: "subsidies", group: "costs" },
  { dataKey: "energySavings", group: "savings" },
  { dataKey: "otherSavings", group: "savings" },
  { dataKey: "servicesCosts", group: "costs" },
  { dataKey: "foreignCapitalCost", group: "costs" },
  { dataKey: "loanRepayment", group: "costs" },
  { dataKey: "maintenanceCosts", group: "costs" },
];

const getDetailedDiagrams = ({ contributorMeta }) =>
  DETAILED_DIAGRAMS_CONFIG.map((item) => ({
    ...item,
    color: contributorMeta[item.dataKey]?.color,
    name: contributorMeta[item.dataKey]?.name,
  }));

const getBaseOption = ({ series, unit, xAxisName, xData, yAxis }) => ({
  animation: true,
  dataZoom: [
    {
      right: 0,
      type: "slider",
      width: 16,
      yAxisIndex: 0,
    },
  ],
  grid: {
    bottom: 70,
    containLabel: true,
    left: 15,
    right: 10,
    top: 55,
  },
  legend: {
    bottom: 0,
    left: 10,
    orient: "horizontal",
    right: 40,
    textStyle: {
      fontFamily: getCSSVar("--font-primary"),
      fontSize: "1.3rem",
      fontWeight: 600,
    },
  },
  series,
  tooltip: {
    trigger: "axis",
    valueFormatter: echartsDefaultTooltipFormatter,
  },
  xAxis: [
    {
      axisPointer: {
        type: "shadow",
      },
      boundaryGap: true,
      data: xData,
      name: xAxisName,
      nameGap: 25,
      nameLocation: "center",
      type: "category",
    },
  ],
  yAxis: [
    {
      axisLine: {
        show: true,
      },
      name: unit,
      nameGap: 10,
      nameLocation: "end",
      nameRotate: 0,
      nameTextStyle: {},
      type: "value",
      ...yAxis,
    },
  ],
});

const buildTotalsSeries = ({ chartColors, t, totalCosts, totalSavings, unit }) => [
  {
    color: chartColors.totalSavings,
    data: totalSavings,
    itemStyle: {
      color: chartColors.totalSavings,
    },
    name: `${t("Modernization.wizard.totalSavings")} [${unit}]`,
    stack: "savings",
    type: "bar",
  },
  {
    color: chartColors.totalCosts,
    data: totalCosts,
    itemStyle: {
      color: chartColors.totalCosts,
    },
    name: `${t("Modernization.wizard.totalCosts")} [${unit}]`,
    stack: "costs",
    type: "bar",
  },
];

const yAxisCustomOptions = {
  min: 0,
};

const buildChartOptions = ({
  aggregatedContributors,
  aggregatedTotals,
  chartColors,
  dataForChart,
  diagrams,
  partitionDiagrams,
  summaryDataForChart,
  t,
  unit,
  yearRange,
}) => {
  const [minYear, maxYear] = yearRange;
  const yearlyXAxisName = t("Modernization.wizard.year");
  const detailedOption = getBaseOption({
    series: [
      ...diagrams.map((diagram) => ({
        color: diagram.color,
        data: dataForChart?.map((d) => d[diagram.dataKey]),
        itemStyle: {
          color: diagram.color,
        },
        name: diagram.name,
        stack: "total",
        type: "bar",
      })),
      {
        data: dataForChart?.map((d) => d.cashFlow),
        lineStyle: {
          color: chartColors.totalTrend,
          width: 2,
        },
        name: `${t("Modernization.wizard.cashflow")} [${unit}]`,
        type: "line",
      },
    ],
    unit,
    xAxisName: yearlyXAxisName,
    xData: dataForChart?.map((d) => d.year),
  });

  const stackedYearlyOption = getBaseOption({
    series: partitionDiagrams.map((diagram) => ({
      color: diagram.color,
      data: dataForChart?.map((d) => Math.abs(Number(d[diagram.dataKey] ?? 0))),
      itemStyle: {
        color: diagram.color,
      },
      ...yAxisCustomOptions,
      name: diagram.name,
      stack: diagram.group,
      type: "bar",
    })),
    unit,
    xAxisName: yearlyXAxisName,
    xData: dataForChart?.map((d) => d.year),
  });

  const totalsYearlyOption = getBaseOption({
    series: buildTotalsSeries({
      chartColors,
      t,
      totalCosts: summaryDataForChart.map((d) => d.totalCosts),
      totalSavings: summaryDataForChart.map((d) => d.totalSavings),
      unit,
    }),
    unit,
    xAxisName: yearlyXAxisName,
    xData: summaryDataForChart.map((d) => d.year),
    yAxis: yAxisCustomOptions,
  });

  const totalsAggregatedOption = getBaseOption({
    series: buildTotalsSeries({
      chartColors,
      t,
      totalCosts: [aggregatedTotals.totalCosts],
      totalSavings: [aggregatedTotals.totalSavings],
      unit,
    }),
    unit,
    xData: [`${t("Modernization.wizard.costsVsSavingsTab")} (${minYear} - ${maxYear})`],
    yAxis: yAxisCustomOptions,
  });

  const stackedAggregatedOption = getBaseOption({
    series: aggregatedContributors.map((item) => ({
      color: item.color,
      data: [item.value],
      itemStyle: {
        color: item.color,
      },
      name: item.name,
      stack: item.group,
      type: "bar",
    })),
    unit,
    xData: [`${t("Modernization.wizard.costsVsSavingsTab")} (${minYear} - ${maxYear})`],
    yAxis: yAxisCustomOptions,
  });

  return {
    detailedOption,
    stackedAggregatedOption,
    stackedYearlyOption,
    totalsAggregatedOption,
    totalsYearlyOption,
  };
};

const getTotalsOption = ({ chartOptions, isPartitions, isYearly }) => {
  if (isPartitions) {
    return isYearly ? chartOptions.stackedYearlyOption : chartOptions.stackedAggregatedOption;
  }
  return isYearly ? chartOptions.totalsYearlyOption : chartOptions.totalsAggregatedOption;
};

export const RetrofitsFinancialChartDiagramWrapper = ({
  activeTabName,
  chartHeight,
  dataForChart,
  isPartitions,
  isYearly,
  unit,
  yearRange,
}) => {
  const { t } = useTranslation();
  const chartColors = useMemo(() => getChartColors(), []);
  const contributorMeta = useMemo(() => getFinancialContributorMeta({ chartColors, unit }), [chartColors, unit]);
  const diagrams = useMemo(() => getDetailedDiagrams({ contributorMeta }), [contributorMeta]);

  const partitionDiagrams = useMemo(
    () =>
      getFinancialPartitionDiagrams({
        contributorMeta,
        defaultCostsColor: chartColors.defaultCosts,
        defaultSavingsColor: chartColors.defaultSavings,
      }),
    [chartColors, contributorMeta]
  );

  const summaryDataForChart = useMemo(() => getFinancialTotalsTimeline(dataForChart ?? []), [dataForChart]);
  const aggregatedTotals = useMemo(() => getAggregatedFinancialTotals(dataForChart ?? []), [dataForChart]);
  const aggregatedContributors = useMemo(
    () => getAggregatedFinancialContributors({ data: dataForChart ?? [], partitionDiagrams }),
    [dataForChart, partitionDiagrams]
  );

  const chartOptions = useMemo(
    () =>
      buildChartOptions({
        aggregatedContributors,
        aggregatedTotals,
        chartColors,
        dataForChart,
        diagrams,
        partitionDiagrams,
        summaryDataForChart,
        t,
        unit,
        yearRange,
      }),
    [
      aggregatedContributors,
      aggregatedTotals,
      chartColors,
      dataForChart,
      yearRange,
      diagrams,
      unit,
      partitionDiagrams,
      summaryDataForChart,
      t,
    ]
  );
  const totalsOption = getTotalsOption({ chartOptions, isPartitions, isYearly });
  const activeOption = activeTabName === "cashflow" ? chartOptions.detailedOption : totalsOption;

  return (
    <div className="w-full" style={{ height: chartHeight }}>
      <DynamicEchartsChart option={activeOption} />
    </div>
  );
};
